import { useReducer } from "react";

import type { SetupTeam } from "@/pages/Tagging/GameSetup/TeamLineupPanel";
import type { IPlayer, TTeamSides } from "@/pages/Tagging/store/types";

/**
 * Game setup state for Scorekeeping V2: each side's team, roster, batting
 * order, pitcher and catcher. Local to the setup page (the legacy tagger
 * keeps the same in its persisted Redux store).
 */

export type SetupTeamState = SetupTeam & { uuid: string };

type SetupAction =
  | {
      type: "setTeam";
      side: TTeamSides;
      name: string;
      uuid: string;
      roster: IPlayer[];
    }
  | { type: "clearTeam"; side: TTeamSides }
  | { type: "setPitcher" | "setCatcher"; side: TTeamSides; player: IPlayer }
  | { type: "clearPitcher" | "clearCatcher"; side: TTeamSides }
  | {
      type: "addToLineup" | "removeFromLineup";
      side: TTeamSides;
      player: IPlayer;
    }
  | { type: "reorderLineup"; side: TTeamSides; lineup: IPlayer[] };

const NO_PLAYER = {} as IPlayer;

const emptyTeam = (): SetupTeamState => ({
  name: "",
  uuid: "",
  roster: [],
  currentLineup: [],
  currentPitcher: NO_PLAYER,
  currentCatcher: NO_PLAYER,
});

const updateTeam = (team: SetupTeamState, action: SetupAction) => {
  switch (action.type) {
    // A new team starts its lineup over
    case "setTeam":
      return {
        ...emptyTeam(),
        name: action.name,
        uuid: action.uuid,
        roster: action.roster,
      };
    case "clearTeam":
      return emptyTeam();
    case "setPitcher":
      return { ...team, currentPitcher: action.player };
    case "clearPitcher":
      return { ...team, currentPitcher: NO_PLAYER };
    case "setCatcher":
      return { ...team, currentCatcher: action.player };
    case "clearCatcher":
      return { ...team, currentCatcher: NO_PLAYER };
    case "addToLineup":
      return { ...team, currentLineup: [...team.currentLineup, action.player] };
    case "removeFromLineup":
      return {
        ...team,
        currentLineup: team.currentLineup.filter(
          (p) => p.uuid !== action.player.uuid,
        ),
      };
    case "reorderLineup":
      return { ...team, currentLineup: action.lineup };
  }
};

const reducer = (
  teams: Record<TTeamSides, SetupTeamState>,
  action: SetupAction,
) => ({ ...teams, [action.side]: updateTeam(teams[action.side], action) });

export const useGameSetupState = () =>
  useReducer(reducer, undefined, () => ({
    away: emptyTeam(),
    home: emptyTeam(),
  }));
